import React, { useEffect, useState } from 'react';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import RingilDialog from '@components/framework/dialog/RingilDialog.tsx';
import { InputTextarea } from 'primereact/inputtextarea';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { CalendarLayoutConfig } from '../model';
import { migrateCalendarLayout } from '../payload';
import { normalizeSurface } from './layoutOps';

interface ExportImportDialogProps {
  visible: boolean;
  /** The layout to show, and the one an import replaces. */
  value: CalendarLayoutConfig;
  onHide: () => void;
  onImport: (next: CalendarLayoutConfig) => void;
  /** Export only — no import button, and the payload is not editable. */
  readOnly?: boolean;
}

/**
 * The layout payload as JSON, for copying one between environments or pasting a hand-written one in.
 *
 * Separate from the designer so the button that opens it can sit with the model's other actions
 * while the dialog stays with the layout it edits.
 */
export const ExportImportDialog: React.FC<ExportImportDialogProps> = ({ visible, value, onHide, onImport, readOnly }) => {
  const { tr } = useTranslationLgs();

  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Re-seeded on each opening: showing the payload from the last time it was open would be a
  // silent export of stale content.
  useEffect(() => {
    if (!visible) return;
    setText(JSON.stringify(value, null, 2));
    setError(null);
  }, [visible]);

  const handleImport = () => {
    try {
      const parsed = migrateCalendarLayout(JSON.parse(text));
      if (!parsed) {
        setError(tr('CalendarLayout.importInvalid', 'That JSON is not a calendar layout configuration.'));
        return;
      }
      onImport({ ...parsed, card: normalizeSurface(parsed.card, 'card'), detail: normalizeSurface(parsed.detail, 'detail') });
      onHide();
    } catch (e) {
      setError(tr('CalendarLayout.importUnparseable', 'Could not parse the JSON: {{message}}', { message: (e as Error).message }));
    }
  };

  return (
    <RingilDialog
      visible={visible}
      onHide={onHide}
      wide
      heading={readOnly ? tr('CalendarLayout.export', 'Export') : tr('CalendarLayout.exportImport', 'Export / import')}
      buttons={
        <>
          {!readOnly && <RingilButton label={tr('CalendarLayout.import', 'Import')} mode="filled" onClick={handleImport} />}
          <RingilButton label={tr('CalendarLayout.close', 'Close')} mode="outlined" onClick={onHide} />
        </>
      }
    >
      <div className="flex flex-col gap-2 w-[640px]">
        <p className="text-xs text-content-secondary m-0">
          {tr('CalendarLayout.exportHint', 'This is the payload the model is stored as — paste one in to replace the layout being edited.')}
        </p>
        <InputTextarea value={text} onChange={e => setText(e.target.value)} rows={18} className="font-mono text-xs" readOnly={readOnly} />
        {error && <div className="text-xs text-complementary-error">{error}</div>}
      </div>
    </RingilDialog>
  );
};
